import React, {
	createContext,
	type InputHTMLAttributes,
	type ReactNode,
	type RefObject,
	useCallback,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useRouter } from 'next/router';

import {
	useCombobox,
	useDebounce,
	useId,
	useRefSetter,
	useRequiredContext,
} from 'hooks';
import type { HTMLAttributes } from 'types';
import { ignorePromiseRejection, is } from 'utils/helpers';

export interface SearchComboboxContextProps {
	/** Ref for a focus target after the search form. */
	afterFormRef: RefObject<HTMLDivElement>;

	/** ID used on the search input field. */
	baseId: string;

	/** Blur the search input field. */
	blurInput: () => void;

	/** Clear the selected combobox option. */
	clearSelectedOption: () => void;

	/** Close the combobox dropdown. */
	closeCombobox: () => void;

	/** Move focus after the search form and its children. */
	focusAfterSearch: () => void;

	/** Focus the search input field. */
	focusInput: () => void;

	/** Get the handler supplied to useFormSubmit. */
	getFormSubmitHandler: () => (() => unknown) | null;

	/** If the combobox dropdown has been opened at least once. */
	hasOpened: boolean;

	/** If the search field has enough text to trigger a search. */
	hasSearchQuery: boolean;

	/** Attributes to add to the search input field. */
	inputProps: InputHTMLAttributes<HTMLInputElement>;

	/** Ref for the search input field. */
	inputRef: RefObject<HTMLInputElement>;

	/** If the combobox dropdown is currently open. */
	isOpen: boolean;

	/** Attributes to add to a div surrounding the combobox options. */
	listboxProps: HTMLAttributes<HTMLDivElement>;

	/** The raw search input text. */
	rawInputValue: string;

	/** The currently active search query (debounced input text). */
	searchQuery: string;

	/** ID of the currently selected combobox option, used for ComboboxOption component. */
	selectedOptionId: string;

	/** Set the search input value. */
	setInputValue: (value: string) => void;

	/** Hook that runs a callback on form submit. */
	useFormSubmit: (handler: () => unknown) => void;
}

const SearchComboboxContext = createContext<
	SearchComboboxContextProps | undefined
>(undefined);

interface Props {
	children: ReactNode;

	/** Search field id. */
	id?: string;

	/** Combobox close callback. */
	onClose?: () => void;

	/** Combobox option activation handler. Options with href are automatically navigated to. */
	onOptionSelect?: (option: HTMLElement) => void;

	/** Minimum number of characters required for a search. */
	searchQueryMinLength?: number;
}

export function SearchComboboxContextProvider({
	children,
	id: idProp,
	onClose,
	onOptionSelect,
	searchQueryMinLength = 3,
}: Props) {
	const router = useRouter();
	const fallbackId = useId('search-combobox');
	const id = idProp ?? fallbackId;

	const [hasOpened, setHasOpened] = useState(false);
	const onComboboxOpen = useCallback(() => {
		if (!hasOpened) {
			setHasOpened(true);
		}
	}, [hasOpened]);

	const onComboboxOptionSelect = useCallback(
		(option: HTMLElement) => {
			onOptionSelect?.(option);
			const link = option.querySelector<HTMLAnchorElement>('[href]');
			if (link?.href) {
				ignorePromiseRejection(router.push(link.href));
			}
		},
		[router, onOptionSelect],
	);

	const {
		clearSelectedOption,
		closeCombobox,
		inputProps,
		inputValue,
		isOpen,
		listboxProps,
		selectedOptionId,
		setInputValue,
	} = useCombobox<HTMLDivElement>({
		baseId: id,
		initialInputValue: is.string(router.query?.query) ? router.query.query : '',
		onClose,
		onOpen: onComboboxOpen,
		onOptionSelect: onComboboxOptionSelect,
	});

	const searchQuery = useDebounce(inputValue, 300);
	// Also check the raw input value to bypass the debounce when the search field
	// is cleared. Only check length 0 for that to avoid flipping to false while
	// replacing the query, e.g.:
	// - Focus the field, it's false.
	// - Search for 'lamps', it's true.
	// - Select the 'lamps' text and start writing 'hammers'.
	// - With a min length check of 3, it would flip back to false while the text
	//   is 'ha', then true again for 'ham' and onwards. This could cause some
	//   flickering layout if the true/false state determines what to render.
	const hasActiveSearchQuery =
		searchQuery.length >= searchQueryMinLength && inputValue.length > 0;

	const inputRef = useRef<HTMLInputElement>(null);
	const afterFormRef = useRef<HTMLDivElement>(null);
	const focusInput = useCallback(() => {
		inputRef.current?.focus();
	}, []);
	const blurInput = useCallback(() => {
		inputRef.current?.blur();
	}, []);
	const focusAfterSearch = useCallback(() => {
		afterFormRef.current?.focus();
	}, []);

	// Pretty awkward, but need to bind form submit in the combobox component
	// while any logic for that handler must be available in the children, like
	// the reverse of an event handler prop. Expose it to the children as a hook.
	const [getFormSubmitHandler, useFormSubmit] = useRefSetter<() => unknown>();

	const value = useMemo(
		() => ({
			baseId: id,
			blurInput,
			clearSelectedOption,
			closeCombobox,
			focusAfterSearch,
			focusInput,
			inputProps,
			getFormSubmitHandler,
			hasOpened,
			inputRef,
			afterFormRef,
			hasSearchQuery: hasActiveSearchQuery,
			isOpen,
			listboxProps,
			rawInputValue: inputValue,
			searchQuery: hasActiveSearchQuery ? searchQuery : '',
			selectedOptionId,
			setInputValue,
			useFormSubmit,
		}),
		[
			blurInput,
			clearSelectedOption,
			closeCombobox,
			focusAfterSearch,
			focusInput,
			inputProps,
			getFormSubmitHandler,
			hasActiveSearchQuery,
			hasOpened,
			id,
			inputValue,
			isOpen,
			listboxProps,
			searchQuery,
			selectedOptionId,
			setInputValue,
			useFormSubmit,
		],
	);

	return (
		<SearchComboboxContext.Provider value={value}>
			{children}
		</SearchComboboxContext.Provider>
	);
}
SearchComboboxContextProvider.displayName = 'SearchComboboxContextProvider';

export function useSearchComboboxContext() {
	return useRequiredContext(SearchComboboxContext, 'SearchComboboxContext');
}
